/**
 * Domain types mirroring the API contract.
 *
 * The string unions are copied deliberately rather than imported from the
 * worker package: the SPA is deployed as static files independently of the
 * API, so a shared build-time import would imply a coupling that does not
 * exist at runtime. If a union changes, the API's own tests fail first.
 */

export const SESSION_TYPES = [
  "kettlebell",
  "bodyweight",
  "kb_and_bodyweight",
  "mobility",
  "pre_run",
  "post_run",
] as const;
export type SessionType = (typeof SESSION_TYPES)[number];

export const SESSION_FOCUSES = [
  "balanced",
  "legs_and_core",
  "eccentric_legs",
  "power_and_carry",
  "core_and_calves",
] as const;
export type SessionFocus = (typeof SESSION_FOCUSES)[number];

export const DIFFICULTY_RATINGS = ["easy", "moderate", "hard", "maxed"] as const;
export type DifficultyRating = (typeof DIFFICULTY_RATINGS)[number];

export const SORENESS_RATINGS = ["none", "mild", "noticeable", "heavy"] as const;
export type SorenessRating = (typeof SORENESS_RATINGS)[number];

export const FEELING_RATINGS = ["fresh", "worked", "drained"] as const;
export type FeelingRating = (typeof FEELING_RATINGS)[number];

export const EXPERIENCE_LEVELS = ["beginner", "some_experience", "experienced"] as const;
export type ExperienceLevel = (typeof EXPERIENCE_LEVELS)[number];

export const MUSCLE_GROUPS = [
  "quads",
  "glutes",
  "hamstrings",
  "upper_push",
  "upper_pull",
  "shoulders",
  "grip",
  "core",
  "central",
  "calves",
] as const;
export type MuscleGroup = (typeof MUSCLE_GROUPS)[number];

/** Human-facing labels. The API stores snake_case; only the UI spells them out. */
export const SESSION_TYPE_LABELS: Record<SessionType, string> = {
  kettlebell: "Kettlebell",
  bodyweight: "Bodyweight",
  kb_and_bodyweight: "Kettlebell + bodyweight",
  mobility: "Mobility",
  pre_run: "Pre-run",
  post_run: "Post-run",
};

export const SESSION_FOCUS_LABELS: Record<SessionFocus, string> = {
  balanced: "Balanced",
  legs_and_core: "Legs and core",
  eccentric_legs: "Eccentric legs",
  power_and_carry: "Power and carry",
  core_and_calves: "Core and calves",
};

export const EXPERIENCE_LEVEL_LABELS: Record<ExperienceLevel, string> = {
  beginner: "Beginner",
  some_experience: "Some experience",
  experienced: "Experienced",
};

export const MUSCLE_GROUP_LABELS: Record<MuscleGroup, string> = {
  quads: "Quads",
  glutes: "Glutes",
  hamstrings: "Hamstrings",
  upper_push: "Upper push",
  upper_pull: "Upper pull",
  shoulders: "Shoulders",
  grip: "Grip",
  core: "Core",
  central: "Central",
  calves: "Calves",
};

export const DIFFICULTY_LABELS: Record<DifficultyRating, string> = {
  easy: "Easy",
  moderate: "Moderate",
  hard: "Hard",
  maxed: "Maxed out",
};

export const FEELING_LABELS: Record<FeelingRating, string> = {
  fresh: "Fresh",
  worked: "Worked",
  drained: "Drained",
};

export const SORENESS_LABELS: Record<SorenessRating, string> = {
  none: "None",
  mild: "Mild",
  noticeable: "Noticeable",
  heavy: "Heavy",
};

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  timezone: string;
  weightUnit: "kg" | "lb";
  /** "user" or "admin" — the API has no boolean isAdmin field. */
  role: string;
  emailVerifiedAt: string | null;
  termsAcceptedAt: string | null;
  termsVersion: string | null;
  /**
   * Set once the user acknowledges the health disclaimer. Legacy gated on this
   * having a value, so onboarding completion is derived from it rather than
   * from a dedicated flag, which the API does not expose.
   */
  healthAcknowledgedAt: string | null;
  healthAckVersion: string | null;
  ageConfirmedAt: string | null;
  createdAt: string;
}

export function isAdmin(user: User | null): boolean {
  return user?.role === "admin";
}

/**
 * Onboarding is complete once the health acknowledgement is recorded, matching
 * the legacy check. Returns false for a null user so callers never gate on a
 * profile that has not loaded yet.
 */
export function hasCompletedOnboarding(user: User | null): boolean {
  return Boolean(user?.healthAcknowledgedAt);
}

export interface Preferences {
  experienceLevel: ExperienceLevel;
  preferredSessionMinutes: number;
  restBetweenExercisesSeconds: number;
  restBetweenRoundsSeconds: number;
  restBetweenSetsSeconds: number;
  defaultRounds: number;
  availableWeightsKg: number[];
  movementsToAvoid: string[];
  timingMultiplier: number;
  setupTimeSeconds: number;
  workoutLayout: "stack" | "overlay";
}

export interface Kettlebell {
  id: string;
  weightKg: number;
  quantity: number;
}

export interface Tag {
  slug: string;
  name: string;
  appliesTo?: string;
}

export interface Exercise {
  id: string;
  name: string;
  description: string | null;
  videoUrl: string | null;
  complexity: string;
  movementType: string;
  equipmentType: string;
  muscleGroups?: string[];
  tags?: Tag[];
}

export interface SessionSet {
  id: string;
  sessionId: string;
  sessionExerciseId: string;
  roundNumber: number;
  setNumber: number;
  repsCompleted: number | null;
  weightUsedKg: number | null;
  durationCompletedSeconds: number | null;
  side: string | null;
  exerciseId: string | null;
  exerciseName: string | null;
  targetReps: number | null;
  createdAt: string;
}

export interface SessionExercise {
  id: string;
  sessionId: string;
  exerciseId: string;
  /** `exerciseName`, not `name` — the API denormalises it onto the row. */
  exerciseName: string;
  orderIndex: number;
  targetSets: number;
  targetReps: number | null;
  targetDurationSeconds: number | null;
  suggestedWeightKg: number | null;
  exerciseMode: string;
  lateralityType: string;
  timeMultiplier: number;
  notes: string | null;
  createdAt: string;
}

/**
 * A session as the API returns it, mirroring `WorkoutSession` in the worker's
 * `types/domain.ts` field for field.
 *
 * Note there is **no `status` field**: state is derived from `startedAt` and
 * `completedAt`, which is what `sessionStatus()` below exists for. Nor are
 * there `sessionType`/`focus` fields — those are planning inputs, not stored
 * on the session.
 */
export interface TrainingSession {
  id: string;
  userId: string;
  sessionName: string | null;
  rounds: number;
  restBetweenExercisesSeconds: number;
  restBetweenRoundsSeconds: number;
  restBetweenSetsSeconds: number;
  startedAt: string | null;
  completedAt: string | null;
  notes: string | null;
  preWorkoutFeeling: string | null;
  perceivedEffort: number | null;
  durationMinutes: number | null;
  srpeLoad: number | null;
  userNotes: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present on list responses only; a single session fetched by id has none. */
  exerciseCount?: number;
  totalSets?: number;
  totalReps?: number;
  durationSeconds?: number;
  exerciseNames?: string[];
  exercises?: SessionExercise[];
  sets?: SessionSet[];
  tags?: Tag[];
}

export type SessionStatus = "planned" | "in_progress" | "completed";

/**
 * Derives session state from the timestamps, since the API stores no status
 * column. Completed is checked first: a completed session also has a
 * `startedAt`, so the reverse order would report every finished session as
 * still in progress.
 */
export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  planned: "Planned",
  in_progress: "In progress",
  completed: "Completed",
};

export function sessionStatus(session: TrainingSession): SessionStatus {
  if (session.completedAt) return "completed";
  if (session.startedAt) return "in_progress";
  return "planned";
}

/** One exercise in a plan preview, which is not yet a persisted session. */
export interface PlannedExercise {
  exerciseId: string;
  exerciseName: string;
  orderIndex: number;
  targetReps: number | null;
  targetDurationSeconds: number | null;
  suggestedWeightKg: number | null;
  exerciseMode: string;
  lateralityType: string;
  timeMultiplier: number;
  secondsPerRep: number | null;
}

/**
 * The result of `/plan/preview`. Deliberately not a `TrainingSession`: nothing
 * has been persisted, so there is no id and the exercises carry no row ids.
 */
export interface PlannedSession {
  exercises: PlannedExercise[];
  rounds: number;
  restBetweenExercisesSeconds: number;
  restBetweenRoundsSeconds: number;
  restBetweenSetsSeconds: number;
  estimatedDurationMinutes: number;
}

/**
 * Describes what an exercise asks for, in a plan preview or a saved session.
 *
 * Exercises are either rep-based or duration-based, so showing reps alone
 * leaves timed movements such as planks and carries with no target at all.
 * Weight appears only when the planner suggested one; it is null whenever the
 * user owns no kettlebell suited to the movement.
 */
export function formatTarget(exercise: {
  targetReps: number | null;
  targetDurationSeconds: number | null;
  suggestedWeightKg: number | null;
}): string {
  const target =
    exercise.targetReps != null
      ? `× ${exercise.targetReps}`
      : exercise.targetDurationSeconds != null
        ? `${exercise.targetDurationSeconds}s`
        : "";

  const weight = exercise.suggestedWeightKg != null ? ` @ ${exercise.suggestedWeightKg} kg` : "";

  return `${target}${weight}`.trim();
}

/** The result of `/plan`, which persists the preview. */
export interface CreatedPlan {
  session: TrainingSession;
  exercises: SessionExercise[];
  plan: PlannedSession;
}

export interface SessionFeedback {
  overallDifficulty: DifficultyRating | null;
  postSessionFeeling: FeelingRating | null;
  sorenessLevel: SorenessRating | null;
  readyToTrainAgain: boolean | null;
}

export interface MuscleFatigue {
  muscleGroup: MuscleGroup;
  fatigueLevel: number;
  confidence: number;
  available: boolean;
}

/** Mirrors DeloadStatus in the API's deload-service.ts. */
export interface DeloadStatus {
  isDeloading: boolean;
  message: string | null;
  daysRemaining: number | null;
  volumeMultiplier: number;
  intensityMultiplier: number;
  reasons: string[];
}

/** Mirrors ReadinessResult in the API's readiness-service.ts. */
export type ReadinessSignal = "green" | "amber" | "gray";

export interface Readiness {
  score: number;
  signal: ReadinessSignal;
  loadScore: number;
  restScore: number;
  weeklyStrengthCount: number;
  weeklySrpe: number;
}

/** Mirrors DeletionStatus in the API's gdpr-repository.ts. */
export interface DeletionStatus {
  deletionRequestedAt: string | null;
  scheduledPurgeAt: string | null;
}

export interface Paged<T> {
  items: T[];
  /** Total matching rows, ignoring limit/offset — needed to size pagination. */
  total: number;
  limit: number;
  offset: number;
}
